import { useQuery } from '@tanstack/react-query';
import { intelligenceRecords, rules } from '@cwos/api-client';
import { Pill, SkeletonSet } from '@cwos/ui-admin';
import type { Severity } from '@cwos/ui-shared';
import { LoadFailure } from '../LoadFailure.js';
import { listPhase } from '../listState.js';
import { useNav } from '../navigation.js';
import { usePermissions } from '../permissions.js';

/**
 * INTELLIGENCE → AUTOMATIONS: the operational face of the rule engine, inside
 * the Water environment. This surface answers "what runs on its own, what did
 * it do, what needs me" — configuration (rule definitions, escalation
 * targets, notification destinations) stays where it lives, in
 * Settings → Rules, reached through the explicit manage link at the top.
 * The two are different rooms on purpose: reading what your automations did
 * is Intelligence; changing what they are allowed to do is Settings, and the
 * environment change on that link marks a real boundary, not a styling gap.
 *
 * MATERIAL: the page is Water. What Phibian is watching and what it suggests
 * automating are glass; the roster of committed rules, the runs that
 * happened and the failures that need a person are solid records
 * (`.card-record`).
 */

const RECENT_RUNS = 12;

function statePill(state: rules.AutomationState, suspended: boolean): Severity {
  if (suspended) return 'look';
  switch (state) {
    case 'active':
      return 'good';
    case 'simulating':
      return 'sky';
    case 'paused':
      return 'look';
    case 'unavailable':
      return 'urgent';
    default:
      return 'plain';
  }
}

function stateLabel(state: rules.AutomationState, suspended: boolean): string {
  if (suspended) return 'Suspended — authority changed';
  switch (state) {
    case 'active':
      return 'Running';
    case 'simulating':
      return 'Watching — simulate only';
    case 'paused':
      return 'Paused';
    case 'draft':
      return 'Not started';
    case 'unavailable':
      return 'Unavailable';
  }
}

function doesSentence(a: rules.AutomationView): string {
  const parts: string[] = [];
  if (a.actions.notifyWorker) parts.push('notifies the worker');
  if (a.actions.notifySupervisor) parts.push('notifies the supervisor');
  if (a.actions.escalationEnabled) {
    parts.push(
      a.actions.escalationAfterMinutes === null
        ? 'escalates'
        : `escalates after ${a.actions.escalationAfterMinutes}m`,
    );
  }
  return parts.length === 0 ? 'records an alert' : parts.join(' · ');
}

export function Automations() {
  const navigate = useNav();
  const { has } = usePermissions();

  const automations = useQuery({
    queryKey: ['rules', 'automations'],
    queryFn: () => rules.automations(),
  });
  const evaluations = useQuery({
    queryKey: ['rules', 'evaluations', RECENT_RUNS],
    queryFn: () => rules.evaluations({ limit: RECENT_RUNS }),
  });
  // Suggested automations ARE recommendation records — advice composed under
  // the automation framework. Same records, same evidence, same withholding
  // rules as the Recommendations room next door.
  const mayReadRecommendations = has('recommendation:read');
  const suggestions = useQuery({
    queryKey: ['intelligence', 'recommendations'],
    queryFn: () => intelligenceRecords.listRecommendations(),
    enabled: mayReadRecommendations,
  });

  const items = automations.data ?? [];
  const suspended = (a: rules.AutomationView) => a.suspendedReason !== null;
  const active = items.filter((a) => a.state === 'active' && !suspended(a));
  const watching = items.filter((a) => a.state === 'simulating' && !suspended(a));
  const needsYou = items.filter(
    (a) => suspended(a) || a.execution.failedActions > 0 || a.execution.openAlerts > 0,
  );
  const runs = evaluations.data ?? [];
  const raised = runs.reduce((sum, run) => sum + (run.alertsGenerated ?? 0), 0);
  const suggested = (suggestions.data?.recommendations ?? []).filter(
    (record) => record.framework_key === 'automation',
  );

  const phase = listPhase(automations, items.length);

  return (
    <div className="rn-col auto-page">
      <header className="rn-col auto-head">
        <div className="auto-head-row">
          <h1>Automations</h1>
          <button
            type="button"
            className="rn-link auto-manage"
            onClick={() => navigate('/settings/rules')}
          >
            Manage automation settings →
          </button>
        </div>
        <p className="rn-muted">
          What runs on its own inside your organization — what it is watching, what it did, and what
          needs a person. Changing what an automation may do lives in Settings.
        </p>
      </header>

      {phase.kind === 'refused' ? (
        <LoadFailure
          error={automations.error}
          message="Automations are not shown to you."
          refusedHeading="You cannot see automations"
        />
      ) : phase.kind === 'failed' ? (
        <LoadFailure
          error={automations.error}
          message="We could not load your automations. This is not a statement that you have none."
          onRetry={() => void automations.refetch()}
        />
      ) : phase.kind === 'loading' ? (
        <SkeletonSet surface="table" rows={5} />
      ) : (
        <>
          {/* The day's reading, as luminous instruments on the water. */}
          <div className="auto-summary" role="group" aria-label="Automation summary">
            <div className="auto-stat">
              <span className="auto-stat-n">{active.length}</span>
              <span className="auto-stat-label">running</span>
            </div>
            <div className="auto-stat">
              <span className="auto-stat-n">{watching.length}</span>
              <span className="auto-stat-label">watching</span>
            </div>
            <div className="auto-stat" data-attention={needsYou.length > 0 ? 'true' : undefined}>
              <span className="auto-stat-n">{needsYou.length}</span>
              <span className="auto-stat-label">need you</span>
            </div>
            <div className="auto-stat">
              <span className="auto-stat-n">{raised}</span>
              <span className="auto-stat-label">
                alerts raised · last {Math.min(runs.length, RECENT_RUNS)} runs
              </span>
            </div>
          </div>

          {/* NEEDS YOU — failures and suspensions are records, so solid. */}
          {needsYou.length > 0 ? (
            <section className="rn-card card-record">
              <div className="rn-card-head">
                <h2 className="rn-card-title">Needs you</h2>
                <span className="rn-muted">Suspended, failing, or holding open alerts.</span>
              </div>
              <ul className="rn-list">
                {needsYou.map((a) => (
                  <li key={a.id} className="auto-needs-row">
                    <span className="rn-strong">{a.name}</span>
                    <span className="rn-muted">
                      {suspended(a)
                        ? 'Suspended: the owner’s authority no longer covers it. Nothing fires until someone re-authorizes it.'
                        : a.execution.failedActions > 0
                          ? `${a.execution.failedActions} failed ${a.execution.failedActions === 1 ? 'action' : 'actions'} — the record of what failed is in Settings → Rules.`
                          : `${a.execution.openAlerts} open ${a.execution.openAlerts === 1 ? 'alert' : 'alerts'} waiting in the attention queue.`}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {/* SUGGESTED — advice about what to automate. Not yet real: glass. */}
          {mayReadRecommendations && suggested.length > 0 ? (
            <section className="rn-card intel-edge auto-suggest">
              <div className="rn-card-head">
                <h2 className="rn-card-title">Suggested automations</h2>
                <span className="rn-muted">
                  Advice Phibian recorded — nothing here runs until a person sets it up.
                </span>
              </div>
              <ul className="rn-list">
                {suggested.map((record) => (
                  <li key={record.id} className="auto-suggest-row">
                    <div className="rn-col auto-suggest-text">
                      <span className="rn-strong">{record.headline}</span>
                      <span className="rn-muted">{record.question}</span>
                    </div>
                    <button
                      type="button"
                      className="rn-link"
                      onClick={() => navigate(`/intelligence/recommendations/${record.id}`)}
                    >
                      Review →
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {/* ACTIVE AUTOMATIONS — committed configuration: solid records. */}
          <section className="rn-card card-record">
            <div className="rn-card-head">
              <h2 className="rn-card-title">Your automations</h2>
              <span className="rn-muted">
                {items.length === 0
                  ? 'None are configured.'
                  : `${items.length} configured · ${active.length} able to run.`}
              </span>
            </div>
            {items.length === 0 ? (
              <p className="rn-muted">
                No automations are configured. Nothing runs on its own in your organization.
              </p>
            ) : (
              <ul className="rn-list">
                {items.map((a) => (
                  <li key={a.id} className="auto-row">
                    <div className="rn-col auto-row-main">
                      <div className="auto-row-head">
                        <span className="rn-strong">{a.name}</span>
                        <Pill variant={statePill(a.state, suspended(a))}>
                          {stateLabel(a.state, suspended(a))}
                        </Pill>
                      </div>
                      <span className="rn-muted">
                        {a.ifSummary ?? a.description ?? a.ruleType} · {doesSentence(a)}
                      </span>
                    </div>
                    <div className="rn-col auto-row-side">
                      <span className="rn-muted">
                        {a.execution.lastActivityAt
                          ? `Last activity ${new Date(a.execution.lastActivityAt).toLocaleString()}`
                          : 'Has not run yet'}
                      </span>
                      <span className="rn-muted">
                        {a.execution.totalAlerts} raised all-time
                        {a.execution.openAlerts > 0 ? ` · ${a.execution.openAlerts} open` : ''}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* RECENT ACTIVITY — runs that happened: solid records. */}
          <section className="rn-card card-record">
            <div className="rn-card-head">
              <h2 className="rn-card-title">Recent activity</h2>
              <span className="rn-muted">The runs themselves, newest first.</span>
            </div>
            {evaluations.isError ? (
              <p className="rn-muted">
                The run history could not be read just now — this is not a statement that nothing
                ran.
              </p>
            ) : runs.length === 0 ? (
              <p className="rn-muted">No runs recorded yet.</p>
            ) : (
              <ul className="rn-list">
                {runs.map((run) => {
                  const name = items.find((a) => a.id === run.ruleId)?.name ?? run.ruleId;
                  return (
                    <li key={run.id} className="auto-run-row">
                      <time className="auto-run-time" dateTime={run.startedAt}>
                        {new Date(run.startedAt).toLocaleString()}
                      </time>
                      <span className="auto-run-text">
                        {name} ·{' '}
                        {run.completedAt === null
                          ? 'still running'
                          : run.status === 'completed'
                            ? `${run.recordsEvaluated ?? '—'} records checked, ${run.alertsGenerated ?? 0} ${(run.alertsGenerated ?? 0) === 1 ? 'alert' : 'alerts'} raised${run.simulated ? ' (simulated — nothing was sent)' : ''}`
                            : 'failed — the run did not finish'}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
